import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Calendar, Clock, AlertCircle, Sparkles, Stethoscope, HeartPulse } from 'lucide-react';
const offices = [{
  name: 'Lincoln Office',
  phone: '402-488-7020',
  phoneHref: 'tel:+14024887020',
  days: 'Mon · Wed · Fri',
  accent: 'sage'
}, {
  name: 'Waverly Office',
  phone: '402-786-2221',
  phoneHref: 'tel:+14027862221',
  days: 'Tue · Thu',
  accent: 'coral'
}];
const visitSections = [{
  icon: Stethoscope,
  title: 'The Exam',
  body: "During your exam, the dentist will thoroughly examine your teeth and gums for signs of tooth decay, gum disease, and other health problems. The dentist may also take X-rays to see what's happening beneath the surface — uncovering dental issues that aren't visible to the naked eye."
}, {
  icon: Sparkles,
  title: 'Professional Cleanings',
  body: 'Your dental hygienist will begin by exploring the surface of your teeth to check for cavities and assess the quality of existing fillings. They will also perform a periodontal exam to make sure your gums adhere tightly to your teeth and that no periodontal disease or bone loss is occurring — followed by a thorough, comfortable cleaning.'
}, {
  icon: HeartPulse,
  title: 'Why Regular Check-ups Matter',
  body: 'The health of your teeth and mouth is very important to the well-being of your entire body. While brushing and flossing at home is essential, a comprehensive exam and cleaning is what keeps your smile looking its best. The American Dental Association recommends visiting your dentist every six months to ensure your teeth stay healthy and your smile stays beautiful.'
}];
export function Appointments() {
  return <>
      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden" data-id="element-225">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-sage-100/50 blur-3xl -z-10" data-id="element-226"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-coral-100/40 blur-3xl -z-10" data-id="element-227"></div>

        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center" data-id="element-228">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-229">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sage-50 text-sage-700 font-medium text-sm mb-6 border border-sage-100" data-id="element-230">
              <Calendar size={16} data-id="element-231" />
              <span data-id="element-232">Scheduling Made Simple</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-serif leading-[1.05] mb-6 text-charcoal-800" data-id="element-233">
              How to make an{' '}
              <span className="text-sage italic" data-id="element-234">appointment</span>.
            </h1>
            <p className="text-lg lg:text-xl text-charcoal-500 max-w-2xl mx-auto leading-relaxed" data-id="element-235">
              The first step toward achieving a beautiful, healthy smile is to
              schedule an appointment. Give the office closest to you a call —
              we're happy to help you find a time that works.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Call to book cards */}
      <section className="pb-16 lg:pb-24" data-id="element-236">
        <div className="max-w-5xl mx-auto px-6 lg:px-8" data-id="element-237">
          <div className="grid md:grid-cols-2 gap-6" data-id="element-238">
            {offices.map((office, i) => <motion.a key={office.name} href={office.phoneHref} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="group bg-white border border-sage-100 rounded-3xl p-8 lg:p-10 hover:shadow-soft hover:-translate-y-1 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-cream" data-id="element-239">
                <div className="flex items-start justify-between mb-6" data-id="element-240">
                  <div data-id="element-241">
                    <p className="text-sm font-semibold tracking-wider uppercase text-sage-600 mb-2" data-id="element-242">
                      {office.name}
                    </p>
                    <p className="font-serif text-3xl md:text-4xl text-charcoal-800 font-medium leading-tight" data-id="element-243">
                      {office.phone}
                    </p>
                  </div>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white shrink-0 ${office.accent === 'sage' ? 'bg-sage' : 'bg-coral'}`} data-id="element-244">
                    <Phone size={24} data-id="element-245" />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-charcoal-500" data-id="element-246">
                  <Clock size={16} data-id="element-247" />
                  <span className="text-sm font-medium" data-id="element-248">{office.days}</span>
                </div>
                <p className="mt-6 text-sage font-medium text-sm group-hover:underline" data-id="element-249">
                  Tap to call →
                </p>
              </motion.a>)}
          </div>

          {/* Cancellation notice */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-8 bg-cream border border-coral-100 rounded-2xl p-6 flex gap-4" data-id="element-250">
            <div className="w-10 h-10 rounded-full bg-coral-50 text-coral flex items-center justify-center shrink-0" data-id="element-251">
              <AlertCircle size={20} data-id="element-252" />
            </div>
            <div data-id="element-253">
              <h3 className="font-serif text-lg font-medium text-charcoal-800 mb-1" data-id="element-254">
                Need to cancel or reschedule?
              </h3>
              <p className="text-charcoal-500 leading-relaxed" data-id="element-255">
                If you need to cancel or reschedule an existing appointment, or
                if you require immediate attention, please contact us as soon as
                possible. We appreciate 24–48 hours notice whenever possible.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What to expect */}
      <section className="py-20 lg:py-24 bg-white border-t border-sage-100" data-id="element-256">
        <div className="max-w-5xl mx-auto px-6 lg:px-8" data-id="element-257">
          <div className="max-w-2xl mb-14" data-id="element-258">
            <h2 className="text-sage font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-259">
              What to Expect
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-charcoal-800 mb-6 leading-tight" data-id="element-260">
              Your visit, end to end.
            </h3>
            <p className="text-lg text-charcoal-500" data-id="element-261">
              Whether it's your first cleaning or your hundredth, here's how a
              typical appointment with us goes.
            </p>
          </div>

          <div className="space-y-6" data-id="element-262">
            {visitSections.map((section, i) => <motion.article key={section.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-cream rounded-3xl p-8 lg:p-10 flex flex-col md:flex-row gap-6 md:gap-10 border border-transparent hover:border-sage-100 transition-colors" data-id="element-263">
                <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center text-sage shrink-0" data-id="element-264">
                  <section.icon size={28} strokeWidth={1.5} data-id="element-265" />
                </div>
                <div data-id="element-266">
                  <h4 className="text-2xl font-serif font-medium text-charcoal-800 mb-3" data-id="element-267">
                    {section.title}
                  </h4>
                  <p className="text-charcoal-600 leading-relaxed" data-id="element-268">
                    {section.body}
                  </p>
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>
    </>;
}